/* Upsell system (ours — Shrine's licensed main.js is not loadable on this fork).
 * Defines the <product-info-upsell> / <cart-drawer-upsell> custom elements rendered
 * by snippets/upsell-block.liquid. Toggle styles (toggle_switch / checkbox / plus)
 * flip data-selected.
 *
 * TWO contexts, TWO behaviors:
 *  - PRODUCT PAGE (<product-info-upsell> etc.): the toggle only marks data-selected;
 *    the main product-form adds the selected upsells alongside the product at ATC time
 *    (product-form.js getSelectedUpsellIds). No cart call happens on toggle here.
 *  - CART DRAWER (<cart-drawer-upsell>): there is NO ATC button to press — the toggle
 *    itself must mutate the cart. Toggle ON => /cart/add.js the variant; toggle OFF =>
 *    /cart/change.js it to qty 0; then re-render the whole #CartDrawer. The added line is
 *    hidden in the items list (upsell-block.liquid emits .cart-item--product-<handle>{
 *    display:none}) so it shows only as the toggle. On (re)render the toggle's selected
 *    state is synced from actual cart contents so it always reflects reality.
 *
 * Classic add_button style needs no JS here — its visible button submits the nested
 * <product-form> directly.
 */
(function () {
  class UpsellElement extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.toggle !== 'true') return;
      this.isCartDrawer = this.tagName === 'CART-DRAWER-UPSELL';

      // In the cart drawer, the toggle's truth is the cart, not the Liquid preselected
      // setting. Sync it on connect so it survives every drawer re-render.
      if (this.isCartDrawer) this.dataset.selected = String(this.isInCart());

      this.querySelectorAll('.upsell-toggle-btn').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (this.isCartDrawer) this.handleCartToggle();
          else this.toggle();
        });
      });
      // Variant dropdowns (multi-variant upsells): keep data-id on the chosen variant.
      const picker = this.querySelector('.upsell__variant-picker');
      if (picker) {
        try {
          this.variants = JSON.parse(picker.querySelector('script[type="application/json"]').textContent);
        } catch (err) {
          this.variants = null;
        }
        picker.querySelectorAll('select.variant-dropdown').forEach((sel) => {
          sel.addEventListener('change', () => this.onVariantChange(picker));
        });
      }
    }

    toggle(force) {
      const next = typeof force === 'boolean' ? force : this.dataset.selected !== 'true';
      this.dataset.selected = String(next);
      this.dispatchEvent(new CustomEvent('upsell:toggled', { bubbles: true, detail: { selected: next } }));
    }

    onVariantChange(picker) {
      if (!this.variants) return;
      const chosen = Array.from(picker.querySelectorAll('select.variant-dropdown')).map((s) => s.value);
      const match = this.variants.find((v) =>
        chosen.every((val, i) => (v['option' + (i + 1)] || null) === val)
      );
      if (match) {
        this.dataset.id = String(match.id);
        const idInput = this.querySelector('product-form input[name="id"]');
        if (idInput) idInput.value = String(match.id);
        if (this.dataset.skipUnavailable === 'true' && !match.available) {
          if (this.isCartDrawer) this.handleCartToggle(false);
          else this.toggle(false);
        }
      }
    }

    /* ---------------- cart-drawer behavior ---------------- */

    // Is this upsell's product currently in the cart? The drawer renders every line item
    // with a cart-item--product-<handle> class (hidden via CSS for cart-drawer upsells).
    isInCart() {
      const handle = this.dataset.handle;
      if (!handle) return false;
      const cls = 'cart-item--product-' + handle;
      return Array.from(document.querySelectorAll('#CartDrawer .cart-item'))
        .some((el) => el.classList.contains(cls));
    }

    // force: optional boolean to force on(true)/off(false); otherwise flips current state.
    async handleCartToggle(force) {
      if (this.dataset.busy === 'true') return;
      const id = this.dataset.id;
      if (!id) return;
      const turnOn = typeof force === 'boolean' ? force : this.dataset.selected !== 'true';
      if (turnOn === (this.dataset.selected === 'true') && typeof force === 'boolean') {
        // already in desired state
        return;
      }

      this.dataset.busy = 'true';
      this.classList.add('upsell--loading');
      this.dataset.selected = String(turnOn); // optimistic; re-render confirms from cart

      try {
        if (turnOn) await this.addToCart(id);
        else await this.removeFromCart(id);
        await this.refreshDrawer();
        // NOTE: refreshDrawer() replaces #CartDrawer innerHTML, so `this` element is now
        // detached and replaced by a fresh instance that syncs its own state from the cart.
      } catch (err) {
        console.error('Cart upsell toggle failed:', err);
        this.dataset.selected = String(!turnOn); // revert optimistic change
        this.dataset.busy = 'false';
        this.classList.remove('upsell--loading');
      }
    }

    async addToCart(id) {
      const item = { id: parseInt(id, 10), quantity: 1 };
      const sp = this.querySelector('product-form input[name="selling_plan"]');
      if (sp && sp.value) item.selling_plan = sp.value;
      const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === 'error' || data.error) {
        throw new Error(data.description || data.message || ('add failed: ' + res.status));
      }
      return data;
    }

    async removeFromCart(id) {
      const res = await fetch((window.routes && window.routes.cart_change_url) || '/cart/change.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: String(id), quantity: 0 })
      });
      if (!res.ok) throw new Error('remove failed: ' + res.status);
      return res.json().catch(() => ({}));
    }

    // Full #CartDrawer re-render via the Section Rendering API. A full replace (vs the
    // theme's items+footer partial) is required because the upsell block can be placed
    // anywhere in the drawer, not only inside .cart-drawer__footer.
    async refreshDrawer() {
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const response = await fetch(`${cartUrl}?section_id=cart-drawer`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const fresh = doc.querySelector('#CartDrawer');
      const current = document.getElementById('CartDrawer');
      if (fresh && current) current.innerHTML = fresh.innerHTML;

      const freshOverlay = doc.querySelector('#CartDrawer-Overlay');
      const currentOverlay = document.getElementById('CartDrawer-Overlay');
      if (freshOverlay && currentOverlay) currentOverlay.innerHTML = freshOverlay.innerHTML;

      // Keep the header cart-count bubble accurate.
      try {
        const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then((r) => r.json());
        const n = cart.item_count || 0;
        document.querySelectorAll('.cart-count-bubble span:first-child').forEach((b) => {
          b.textContent = n < 100 ? n : '99+';
        });
        const drawerEl = document.querySelector('cart-drawer');
        if (drawerEl) drawerEl.classList.toggle('is-empty', n === 0);
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: cart }));
      } catch (e) { /* count bubble best-effort */ }
    }
  }

  ['product-info-upsell', 'cart-drawer-upsell', 'main-product-upsell', 'featured-product-upsell'].forEach((tag) => {
    if (!customElements.get(tag)) {
      customElements.define(tag, class extends UpsellElement {});
    }
  });
})();
